<script lang="ts" setup>
import type { StaticPageImage } from '~/types/StaticPageList';

defineProps<{ images: StaticPageImage[]; bordered?: boolean }>();

function getSourceForDefault(image) {
  if (!image) return null;
  const conversions = image.conversions_webp;
  if (conversions.thumb_180) {
    if (conversions.thumb_360) {
      return `${conversions.thumb_180}, ${conversions.thumb_360} 2x`;
    }
    return conversions.thumb_180;
  }
  return image.url;
}

function getImageJpg(image) {
  if (!image) return null;
  const { conversions } = image;
  if (conversions?.thumb_180) {
    return conversions.thumb_180;
  }
  if (image.url) {
    return image.url;
  }
  return image.url;
}
</script>

<template>
  <div
    v-if="images?.length"
    class="constructor-image-grid"
  >
    <AppLink
      v-for="image in images"
      :key="image.url"
      :to="image.url"
      class="constructor-image-grid__item"
    >
      <picture
        :class="{ 'constructor-image-grid__item-img-box--bordered': bordered }"
        class="constructor-image-grid__item-img-box"
      >
        <source
          :srcset="getSourceForDefault(image)"
          type="image/webp"
        />
        <source
          :srcset="getImageJpg(image)"
          type="image/jpeg"
        />
        <img
          :alt="image.properties?.alt || image?.text || image.properties?.title"
          :src="image.url"
          class="constructor-image-grid__item-image"
          loading="lazy"
        />
      </picture>

      {{ image.text }}
    </AppLink>
  </div>
</template>

<style lang="scss" scoped>
.constructor-image-grid {
  @include inner-content;

  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;

  @include screen-sm {
    grid-template-columns: repeat(3, 1fr);
  }

  @include screen-md {
    grid-template-columns: repeat(4, 1fr);
  }

  &__item {
    display: flex;
    flex-direction: column;
    gap: 16px;
    align-items: center;
    font-size: $text-sm;
  }

  &__item-img-box {
    overflow: hidden;
    width: 100%;

    &--bordered {
      border: 1px solid $hell-black;
      border-radius: 12px;
    }
  }

  &__item-image {
    object-fit: cover;
    width: 100%;
    border-radius: 12px;
    aspect-ratio: 110/60;
  }
}
</style>
